import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useCallback, useState, } from 'react';
import { Label } from './Typography';
const sizeStyles = {
    sm: { height: 28, padding: '0 var(--space-2)', fontSize: 'var(--font-size-sm)' },
    md: { height: 36, padding: '0 var(--space-3)', fontSize: 'var(--font-size-base)' },
    lg: { height: 44, padding: '0 var(--space-4)', fontSize: 'var(--font-size-md)' },
};
function baseInputStyle(focused, error) {
    return {
        fontFamily: 'var(--font-family)',
        backgroundColor: 'var(--color-surface-sunken)',
        color: 'var(--color-text)',
        border: `1px solid ${error ? 'var(--color-danger)' : focused ? 'var(--color-accent)' : 'var(--color-border)'}`,
        borderRadius: 'var(--radius-md)',
        outline: 'none',
        boxShadow: focused ? 'var(--shadow-focus)' : 'none',
        transition: `border-color var(--transition-fast), box-shadow var(--transition-fast)`,
        width: '100%',
    };
}
export const Input = forwardRef(function Input({ size = 'md', error, style, onFocus, onBlur, ...rest }, ref) {
    const [focused, setFocused] = useState(false);
    const handleFocus = useCallback((e) => {
        setFocused(true);
        onFocus?.(e);
    }, [onFocus]);
    const handleBlur = useCallback((e) => {
        setFocused(false);
        onBlur?.(e);
    }, [onBlur]);
    const s = {
        ...baseInputStyle(focused, error),
        ...sizeStyles[size],
        ...style,
    };
    return (_jsx("input", { ref: ref, style: s, onFocus: handleFocus, onBlur: handleBlur, ...rest }));
});
export const Textarea = forwardRef(function Textarea({ error, resize = 'vertical', style, onFocus, onBlur, ...rest }, ref) {
    const [focused, setFocused] = useState(false);
    const handleFocus = useCallback((e) => {
        setFocused(true);
        onFocus?.(e);
    }, [onFocus]);
    const handleBlur = useCallback((e) => {
        setFocused(false);
        onBlur?.(e);
    }, [onBlur]);
    const s = {
        ...baseInputStyle(focused, error),
        padding: 'var(--space-3)',
        fontSize: 'var(--font-size-base)',
        lineHeight: 'var(--line-height-normal)',
        resize,
        minHeight: 80,
        ...style,
    };
    return (_jsx("textarea", { ref: ref, style: s, onFocus: handleFocus, onBlur: handleBlur, ...rest }));
});
export const Select = forwardRef(function Select({ size = 'md', error, style, onFocus, onBlur, children, ...rest }, ref) {
    const [focused, setFocused] = useState(false);
    const handleFocus = useCallback((e) => {
        setFocused(true);
        onFocus?.(e);
    }, [onFocus]);
    const handleBlur = useCallback((e) => {
        setFocused(false);
        onBlur?.(e);
    }, [onBlur]);
    const wrapperStyle = {
        position: 'relative',
        display: 'inline-block',
        width: '100%',
    };
    const selectStyle = {
        ...baseInputStyle(focused, error),
        ...sizeStyles[size],
        appearance: 'none',
        paddingRight: 'var(--space-8)',
        cursor: 'pointer',
        ...style,
    };
    const chevronStyle = {
        position: 'absolute',
        right: 'var(--space-3)',
        top: '50%',
        transform: 'translateY(-50%)',
        pointerEvents: 'none',
        color: 'var(--color-text-muted)',
    };
    return (_jsxs("div", { style: wrapperStyle, children: [_jsx("select", { ref: ref, style: selectStyle, onFocus: handleFocus, onBlur: handleBlur, ...rest, children: children }), _jsx("svg", { style: chevronStyle, width: "12", height: "12", viewBox: "0 0 12 12", fill: "none", children: _jsx("path", { d: "M3 4.5L6 7.5L9 4.5", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round" }) })] }));
});
export const SearchInput = forwardRef(function SearchInput({ size = 'md', value, onChange, onClear, style, onFocus, onBlur, ...rest }, ref) {
    const [focused, setFocused] = useState(false);
    const handleFocus = useCallback((e) => {
        setFocused(true);
        onFocus?.(e);
    }, [onFocus]);
    const handleBlur = useCallback((e) => {
        setFocused(false);
        onBlur?.(e);
    }, [onBlur]);
    const wrapperStyle = {
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        width: '100%',
    };
    const inputStyle = {
        ...baseInputStyle(focused),
        ...sizeStyles[size],
        paddingLeft: 'var(--space-8)',
        paddingRight: value ? 'var(--space-8)' : undefined,
        ...style,
    };
    const iconStyle = {
        position: 'absolute',
        left: 'var(--space-3)',
        pointerEvents: 'none',
        color: 'var(--color-text-faint)',
    };
    const clearStyle = {
        position: 'absolute',
        right: 'var(--space-2)',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 20,
        height: 20,
        border: 'none',
        background: 'none',
        color: 'var(--color-text-faint)',
        cursor: 'pointer',
        borderRadius: 'var(--radius-full)',
        padding: 0,
        fontFamily: 'var(--font-family)',
    };
    return (_jsxs("div", { style: wrapperStyle, children: [_jsxs("svg", { style: iconStyle, width: "14", height: "14", viewBox: "0 0 14 14", fill: "none", children: [_jsx("circle", { cx: "6", cy: "6", r: "4.5", stroke: "currentColor", strokeWidth: "1.5" }), _jsx("path", { d: "M9.5 9.5L13 13", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round" })] }), _jsx("input", { ref: ref, type: "search", value: value, onChange: (e) => onChange(e.target.value), style: inputStyle, onFocus: handleFocus, onBlur: handleBlur, ...rest }), value && (_jsx("button", { type: "button", style: clearStyle, onClick: () => { onChange(''); onClear?.(); }, tabIndex: -1, "aria-label": "Clear search", children: "\u00D7" }))] }));
});
export function FormField({ label, htmlFor, hint, error, children }) {
    const wrapperStyle = {
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-1)',
    };
    const hintStyle = {
        fontFamily: 'var(--font-family)',
        fontSize: 'var(--font-size-xs)',
        color: 'var(--color-text-faint)',
        lineHeight: 'var(--line-height-normal)',
    };
    const errorStyle = {
        fontFamily: 'var(--font-family)',
        fontSize: 'var(--font-size-xs)',
        color: 'var(--color-danger)',
        lineHeight: 'var(--line-height-normal)',
    };
    return (_jsxs("div", { style: wrapperStyle, children: [_jsx(Label, { htmlFor: htmlFor, children: label }), children, error && _jsx("span", { style: errorStyle, children: error }), !error && hint && _jsx("span", { style: hintStyle, children: hint })] }));
}
